import desktopProductImage from '../assets/images/1.png';
import desktopBackgroundImage from '../assets/images/2.png';
import mobileBackgroundImage from '../assets/images/3.png';
import mobileProductImage from '../assets/images/4.png';
import { products } from '../utils/productData';
import { calculateDiscountPercentage, calculateSavingsAmount } from '../utils/pricing';

export type CampaignTheme = 'glow-edit';
export type CampaignMediaType = 'image';

export type CampaignCTA = {
  label: string;
  href: string;
};

export type CampaignColors = {
  primary: string;
  secondary: string;
  accent: string;
  deep: string;
  soft: string;
};

const glowCombo = products.find((product) => product.id === 3);
const maxDiscount = Math.max(
  ...products.map((product) => calculateDiscountPercentage(product.originalPrice, product.price) ?? 0)
);
const formatCurrency = (amount: number) => `₹${amount.toLocaleString('en-IN')}`;
const glowComboPrice = glowCombo ? formatCurrency(glowCombo.price) : '';
const glowComboOriginalPrice = glowCombo?.originalPrice ? formatCurrency(glowCombo.originalPrice) : '';
const glowComboSavings = glowCombo ? calculateSavingsAmount(glowCombo.originalPrice, glowCombo.price) : null;
const glowComboDiscount = glowCombo ? calculateDiscountPercentage(glowCombo.originalPrice, glowCombo.price) : null;

export type CampaignConfig = {
  campaignName: string;
  campaignLabel: string;
  campaignTheme: CampaignTheme;
  heading: string;
  subHeading: string;
  description: string;
  accentColors: CampaignColors;
  gradientColors: { hero: string; announcement: string };
  heroDesktopImage: string;
  heroTabletImage: string;
  heroMobileImage: string;
  heroDesktopBackgroundImage: string;
  heroMobileBackgroundImage: string;
  featuredPromoMedia: string;
  heroImageFit: 'contain' | 'cover';
  heroImagePosition: string;
  heroMediaType: CampaignMediaType;
  heroMediaAlt: string;
  offerPercentage: number;
  announcementText: string;
  primaryCTA: CampaignCTA;
  secondaryCTA: CampaignCTA;
  featuredComboName: string;
  featuredComboPrice: string;
  featuredComboOriginalPrice: string;
  featuredComboSavings: string;
  featuredComboDiscount: number | null;
  featuredComboCtaText: string;
  featuredComboHref: string;
  offerChips: string[];
  trustPoints: string[];
  showAnnouncementBar: boolean;
  showOfferBadge: boolean;
  showFloatingComboCard: boolean;
  showTrustStrip: boolean;
  showSecondaryCTA: boolean;
  showRating: boolean;
  showSavings: boolean;
  showOriginalPrice: boolean;
  showPriceCard: boolean;
  backgroundEffects: { glow: boolean };
};

export const campaign: CampaignConfig = {
  campaignName: 'MaySecret The Glow Edit',
  campaignLabel: 'MaySecret • The Glow Edit',
  campaignTheme: 'glow-edit',
  heading: 'Your Glow. Your Ritual.',
  subHeading: 'Everyday Radiance, Inspired by Korean Skincare.',
  description: 'Discover thoughtful skincare essentials designed to make your daily routine feel special.',
  accentColors: {
    primary: '#741B38',
    secondary: '#3A0B19',
    accent: '#D6A94D',
    deep: '#10070B',
    soft: '#F8E8C4',
  },
  gradientColors: {
    hero: 'radial-gradient(circle at 76% 44%, rgba(125, 27, 59, 0.82), transparent 33%), linear-gradient(122deg, #10070B 0%, #2B0A19 48%, #12070B 100%)',
    announcement: 'linear-gradient(90deg, #19080E 0%, #6D1738 50%, #19080E 100%)',
  },
  heroDesktopImage: desktopProductImage,
  heroTabletImage: desktopProductImage,
  heroMobileImage: mobileProductImage,
  heroDesktopBackgroundImage: desktopBackgroundImage,
  heroMobileBackgroundImage: mobileBackgroundImage,
  featuredPromoMedia: mobileProductImage,
  heroImageFit: 'contain',
  heroImagePosition: 'center',
  heroMediaType: 'image',
  heroMediaAlt: 'MaySecret Rice Brightening Serum and Sunscreen Spray in a premium festive skincare composition',
  offerPercentage: maxDiscount,
  announcementText: `MaySecret Festive Glow • Up to ${maxDiscount}% Off • Combo Packs from ₹899 • Free Shipping • Cash on Delivery`,
  primaryCTA: { label: 'Shop Skincare', href: '/shop' },
  secondaryCTA: { label: 'Explore Glow Combos', href: '/shop?category=combo' },
  featuredComboName: 'Glow Combo Pack',
  featuredComboPrice: glowComboPrice,
  featuredComboOriginalPrice: glowComboOriginalPrice,
  featuredComboSavings: glowComboSavings === null ? '' : `Save ${formatCurrency(glowComboSavings)}`,
  featuredComboDiscount: glowComboDiscount,
  featuredComboCtaText: 'Shop Glow Combo',
  featuredComboHref: '/product/3',
  offerChips: [`Up to ${maxDiscount}% Off`, 'Combo Packs from ₹899', 'Free Shipping', 'Cash on Delivery'],
  trustPoints: ['Korean Inspired', 'Premium Ingredients', 'Cruelty Free', 'Customer Favourite'],
  showAnnouncementBar: true,
  showOfferBadge: true,
  showFloatingComboCard: true,
  showTrustStrip: true,
  showSecondaryCTA: true,
  showRating: true,
  showSavings: true,
  showOriginalPrice: true,
  showPriceCard: true,
  backgroundEffects: { glow: true },
};
